import React, { useState, useEffect } from 'react';
import { presetExpensesService } from '../../services/presetExpensesService';

const PresetExpensesManager = ({ isOpen, onClose }) => {
  const [presetExpenses, setPresetExpenses] = useState([]);
  const [newExpense, setNewExpense] = useState({ name: '', amount: '' });
  const [editingExpense, setEditingExpense] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load preset expenses when component mounts or opens
  useEffect(() => {
    if (isOpen) {
      loadPresetExpenses();
    }
  }, [isOpen]);

  const loadPresetExpenses = () => {
    setLoading(true);
    try {
      const expenses = presetExpensesService.getPresetExpenses();
      setPresetExpenses(expenses);
    } catch (error) {
      console.error('Error loading preset expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = () => {
    if (!newExpense.name.trim() || !newExpense.amount) {
      alert('Please fill in both name and amount');
      return;
    }

    const addedExpense = presetExpensesService.addPresetExpense(newExpense);
    if (addedExpense) {
      setPresetExpenses([...presetExpenses, addedExpense]);
      setNewExpense({ name: '', amount: '' });
    } else {
      alert('Failed to add preset expense');
    }
  };

  const handleUpdateExpense = () => {
    if (!editingExpense.name.trim() || !editingExpense.amount) {
      alert('Please fill in both name and amount');
      return;
    }

    const updatedExpense = presetExpensesService.updatePresetExpense(editingExpense.id, {
      name: editingExpense.name,
      amount: editingExpense.amount
    });

    if (updatedExpense) {
      setPresetExpenses(presetExpenses.map(expense => 
        expense.id === editingExpense.id ? updatedExpense : expense
      ));
      setEditingExpense(null);
    } else {
      alert('Failed to update preset expense');
    }
  };

  const handleDeleteExpense = (id) => {
    if (window.confirm('Are you sure you want to delete this preset expense?')) {
      if (presetExpensesService.deletePresetExpense(id)) {
        setPresetExpenses(presetExpenses.filter(expense => expense.id !== id));
      } else {
        alert('Failed to delete preset expense');
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Number(amount));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Manage Preset Expenses</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Add New Expense Form */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Preset Expense</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expense Name
              </label>
              <input
                type="text"
                value={newExpense.name}
                onChange={(e) => setNewExpense({ ...newExpense, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                placeholder="e.g., Travel, Supplies, Equipment"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newExpense.amount}
                  onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleAddExpense}
                disabled={!newExpense.name.trim() || !newExpense.amount}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-sky-600 border border-transparent rounded-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Preset
              </button>
            </div>
          </div>
        </div>

        {/* Existing Preset Expenses */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Existing Preset Expenses ({presetExpenses.length})
          </h3>
          
          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading preset expenses...</p>
            </div>
          ) : presetExpenses.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z" />
              </svg>
              <h3 className="text-sm font-medium text-gray-900 mb-1">No preset expenses</h3>
              <p className="text-sm text-gray-500">Add your first preset expense using the form above.</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {presetExpenses.map((expense) => (
                  <li key={expense.id} className="px-6 py-4">
                    {editingExpense && editingExpense.id === expense.id ? (
                      /* Edit Mode */
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        <div>
                          <input
                            type="text"
                            value={editingExpense.name}
                            onChange={(e) => setEditingExpense({ ...editingExpense, name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                          />
                        </div>
                        <div>
                          <div className="relative">
                            <span className="absolute left-3 top-2 text-gray-500">$</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={editingExpense.amount}
                              onChange={(e) => setEditingExpense({ ...editingExpense, amount: e.target.value })}
                              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                            />
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={handleUpdateExpense}
                            className="px-3 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingExpense(null)}
                            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* View Mode */
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">{expense.name}</h4>
                          <p className="text-sm text-gray-500">{formatCurrency(expense.amount)}</p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setEditingExpense(expense)}
                            className="text-sky-600 hover:text-sky-900 text-sm font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteExpense(expense.id)}
                            className="text-red-600 hover:text-red-900 text-sm font-medium"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 text-sm font-medium text-white bg-sky-600 border border-transparent rounded-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default PresetExpensesManager;